import { useState, useEffect } from "react";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, FileJson, RotateCcw, Loader2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { getHistory, pollGeneration, getExportUrl } from "@/lib/api";

export default function HistoryTab({ onReopenJob, currentDb }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    let mounted = true;
    getHistory()
      .then((data) => {
        if (mounted) setJobs(data.history || []);
      })
      .catch(() => {
        if (mounted) setError(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const handleReopen = async (job) => {
    if (currentDb) {
      const confirmed = window.confirm("Replace the current database with this historical one?");
      if (!confirmed) return;
    }
    setLoadingId(job.job_id);
    try {
      const result = await pollGeneration(job.job_id);
      if (result.status !== "completed" || !result.data) {
        toast.error("This database has expired.");
      } else {
        onReopenJob(result);
      }
    } catch {
      toast.error("This database has expired and is no longer available.");
    } finally {
      setLoadingId(null);
    }
  };

  const getExpiryInfo = (expiresAt, isExpired) => {
    if (isExpired) return { label: "Expired", color: "#ef4444" };
    const diffMs = new Date(expiresAt) - new Date();
    const diffHours = diffMs / (1000 * 60 * 60);
    if (diffHours < 2) return { label: "< 2h left", color: "#ef4444" };
    if (diffHours < 24) return { label: "< 24h left", color: "#eab308" };
    return { label: `${Math.ceil(diffHours / 24)}d left`, color: "var(--sdb-text-muted)" };
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });

  if (loading) {
    return (
      <div className="space-y-4 py-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-sm" style={{ background: "var(--sdb-surface)" }} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center font-mono text-sm" style={{ color: "var(--sdb-text-muted)" }}>
        Failed to load history
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center font-mono text-sm" style={{ color: "var(--sdb-text-muted)" }}>
        No generations yet. Describe a database above to get started.
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {jobs.map((job) => {
        const isExpired = new Date(job.expires_at) <= new Date();
        const expiryInfo = getExpiryInfo(job.expires_at, isExpired);
        const isProcessing = loadingId === job.job_id;

        return (
          <div
            key={job.job_id}
            className="border-b py-4 transition-opacity"
            style={{ borderColor: "var(--sdb-border)", opacity: isExpired ? 0.5 : 1 }}
          >
            {/* Prompt */}
            <div className="mb-2 text-sm font-medium truncate" style={{ color: "var(--sdb-text)" }}>
              {job.prompt}
            </div>

            {/* Metadata */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs" style={{ color: "var(--sdb-text-muted)" }}>
                {formatDate(job.created_at)}
              </span>
              <Badge
                variant="outline"
                className="rounded-sm font-normal font-mono text-[10px]"
                style={{ borderColor: "var(--sdb-border)", color: "var(--sdb-text-secondary)" }}
              >
                {job.user_charge_cents === 0 ? "Free" : `$${(job.user_charge_cents / 100).toFixed(2)}`}
              </Badge>
              <Badge
                variant="outline"
                className="rounded-sm font-normal font-mono text-[10px]"
                style={{ borderColor: expiryInfo.color, color: expiryInfo.color }}
              >
                <Clock className="mr-1 h-3 w-3" strokeWidth={1.5} />
                {expiryInfo.label}
              </Badge>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                {[["sql", FileText], ["csv", FileSpreadsheet], ["json", FileJson]].map(([fmt, Icon]) => (
                  <Button
                    key={fmt}
                    variant="ghost"
                    size="sm"
                    className="h-7 rounded-sm px-2 font-mono text-[10px]"
                    style={{ color: "var(--sdb-text-secondary)" }}
                    disabled={isExpired}
                    onClick={() => window.open(getExportUrl(job.job_id, fmt), "_blank")}
                  >
                    <Icon className="mr-1 h-3 w-3" strokeWidth={1.5} />
                    {fmt.toUpperCase()}
                  </Button>
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                className="h-7 rounded-sm px-3 font-mono text-[10px]"
                style={{ borderColor: "var(--sdb-border)", color: "var(--sdb-text)" }}
                onClick={() => handleReopen(job)}
                disabled={isExpired || isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
                )}
              </Button>
            </div>
          </div>
        );
      })}

      <div className="mt-4 border-t pt-4 text-center" style={{ borderColor: "var(--sdb-border)" }}>
        <p className="font-mono text-[10px] tracking-[1px] uppercase" style={{ color: "var(--sdb-text-muted)" }}>
          Showing last 20 generations · Free: 24h · Paid: 30 days
        </p>
      </div>
    </div>
  );
}
